import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { useEffect, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import ClientContext from 'contexts/ClientContext'
import classNames from 'classnames'
import style from './ContractSelection.module.scss'
import DocumentText from 'components/assets/icons/DocumentText'
import Card from 'components/common/Card/Card'
import ShieldLogo from 'components/assets/icons/ShieldLogo'
import InfoMessage from 'components/common/InfoMessage/InfoMessage'
import PageTitle from 'components/common/PageTitle/PageTitle'
import PageSubtitle from 'components/common/PageSubtitle/PageSubtitle'
import { sortContractsByStatus } from 'helpers/contract'
import api from 'helpers/api'
import Status from 'components/common/Status/Status'
import { ADHESION_STATUS_TYPE, isAdhesionInactive } from 'constants/adhesionStatus'

const ContractSelection = () => {
  const { t, i18n: { language } } = useTranslation()
  const navigate = useNavigate()
  const { state } = useLocation()
  const { isColabelAllowed, contractTypeId, partnerId, distributorId,color, colabelNeutralDark } = useContext(ClientContext)
  const [contracts, setContracts] = useState([])

  useEffect(() => {
    const getContracts = async () => {
      try {
        const res = isColabelAllowed
          ? await api.get({ url: `/adhesions/${contractTypeId}/${partnerId}/${distributorId}` })
          : await api.get({ url: '/adhesions/client' })
        setContracts(sortContractsByStatus(res.adhesions.filter(item => !item.cancelation)))
      } catch (error) {
        console.log('Fetch contracts error')
        navigate('/contracts')
      }
    }
    getContracts()
  }, [navigate, isColabelAllowed, contractTypeId, partnerId, distributorId, language])

  const isContractListEmpty = !contracts || !contracts?.length
  if (isContractListEmpty) return
  const singleContractTargeted = state?.singleContract ? state.contract : contracts[0]
  const { claimsPersonConcerned, claimsClientInfo } = singleContractTargeted.contractType.customizationCardVisibleStatus || {}

  return contracts.length === 1 || state?.singleContract
  ? <Navigate
      to={'/claims/new/' + (claimsPersonConcerned  === false || claimsClientInfo === false
        ? claimsClientInfo === false ? 'circumstances' : 'contacts'
        : 'insured-person')
      }
      state={
        {
          contract: singleContractTargeted,
          step: claimsPersonConcerned === false || claimsClientInfo === false
            ? claimsClientInfo === false ? 3 : 2
            : 1,
          fromClaim: true,
          claim: claimsClientInfo === false && {
            firstname: contracts[0]?.firstname,
            lastname: contracts[0]?.lastname,
            birthdate: contracts[0]?.birthdate,
            placeOfBirth: contracts[0]?.placeOfBirth,
            countryOfBirth: contracts[0]?.countryOfBirth,
            address: contracts[0]?.address,
            zipcode: contracts[0]?.zipcode,
            city: contracts[0]?.city,
            country: contracts[0]?.country,
            additionalAddress: contracts[0]?.additionalAddress,
            email: contracts[0]?.email,
            phone: contracts[0]?.phone
          },
          claimsPersonConcerned,
          claimsClientInfo,
          singleContract: true
        }
      } replace
    />
  : (
    <>
      <PageTitle text={t('claim:applySteps.contract.title')} />
      <PageSubtitle text={t('claim:applySteps.contract.subtitle')} />
      {isContractListEmpty && (
        <InfoMessage
          className={style['info-message']}
          label={t('claim:applySteps.contract.empty')}
          prefix={<ShieldLogo styles={color && {fill: colabelNeutralDark}} />}
        />
      )}

      <div className={classNames(style.items, isContractListEmpty && style.empty)}>
        {!isContractListEmpty &&
          contracts.map((item, index) => {
            return (
              <Card
                className={style.item}
                inactive={item.cancelation || isAdhesionInactive(item.adhesionStatus)}
                content={
                  <div className={style['item-content']}>
                    <div>
                      {item.rateType.dashboardPackageDescription || item.contractType.dashboardDescription}
                      {item.contractType.displayCardNumber &&
                        <ul className={style.cards}>
                          {item.virtualCard && <li>{t('common:virtualCard')} : {`**** **** **** ${item.virtualCard.slice(-4)}`}</li>}
                          {item.physicalCard && <li>{t('common:physicalCard')} : {`**** **** **** ${item.physicalCard.slice(-4)}`}</li>}
                          {item.physicalCard2 && <li>{t('common:physicalCard2')} : {`**** **** **** ${item.physicalCard2.slice(-4)}`}</li>}
                        </ul>
                      }
                    </div>
                    {item.adhesionStatus && (
                      <Status
                        label={t(`common:adhesionStatus.${item.adhesionStatus}`, { defaultValue: item.adhesionStatus })}
                        type={ADHESION_STATUS_TYPE[item.adhesionStatus] || 'adhesionSigned'}
                      />
                    )}
                  </div>
                }
                key={`contract-item-${index}`}
                title={
                  item.rateType?.dashboardPackageName 
                    ? <>{item.rateType.dashboardPackageName} - <i>{item.idTransaction}</i></>
                    : <>{item.contractType.dashboardName} - <i>{item.idTransaction}</i></>
                }
                titleIcon={<DocumentText styles={color && {fill: colabelNeutralDark}} />}
                onClick={() => navigate('insured-person', { state: {
                  contract: item,
                  step: 1,
                  fromClaim: true,
                  claimsPersonConcerned: item.contractType.customizationCardVisibleStatus.claimsPersonConcerned,
                  claimsClientInfo: item.contractType.customizationCardVisibleStatus.claimsClientInfo
                }})}
              />
            )
          })}
      </div>
    </>
  )
}

export default ContractSelection
